'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Wallet } from 'lucide-react';
import AgeVerificationModal from '@/app/components/AgeVerificationModal';
import KickIcon from '@/app/components/Navbar/KickIcon';
import { useKickLogin } from '@/app/hooks/useKickLogin';

/**
 * Logged-out counterpart of <WalletDropdown/>: keeps the wallet in the same spot
 * for guests, but shows a login prompt instead of balances.
 */
export default function GuestWalletDropdown() {
    const [open, setOpen] = useState(false);
    const [showAgeModal, setShowAgeModal] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const doKickLogin = useKickLogin();

    useEffect(() => {
        if (!open) return;

        const onClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label="Wallet"
                aria-expanded={open}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-xl text-gray-200 hover:bg-gray-800/70 transition-colors"
            >
                <Wallet size={16} className="text-emerald-400" />
                <span className="font-semibold">Wallet</span>
                <ChevronDown
                    size={14}
                    className={`text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden z-50">
                    <div className="flex flex-col items-center gap-3 px-4 py-4">
                        <p className="text-sm text-gray-400 text-center leading-snug">
                            Login with Kick to access your wallet
                        </p>
                        <button
                            onClick={() => { setOpen(false); setShowAgeModal(true); }}
                            className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-xl font-bold text-sm text-white transition-all duration-200 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 hover:shadow-lg hover:shadow-emerald-500/20"
                            aria-label="Login with Kick"
                        >
                            <KickIcon />
                            Login with Kick
                        </button>
                    </div>
                </div>
            )}

            {showAgeModal && (
                <AgeVerificationModal
                    onConfirm={() => { setShowAgeModal(false); doKickLogin(); }}
                    onClose={() => setShowAgeModal(false)}
                />
            )}
        </div>
    );
}
